/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.isInitialLoad = true; // Adding a flag for image changes on inital load

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // Update label text immediately
      VariantPicker.updateLabelText(evt);

      // For high-variant products, always fetch to get the correct variant
      const selectedOptionValueIds = this.getSelectedOptionValueIds();
      if (selectedOptionValueIds.length > 0 && this.data.optionValues) {
        // Set a temporary variant with basic info while we fetch
        const lastOptionValueId = selectedOptionValueIds[selectedOptionValueIds.length - 1];
        if (this.data.optionValues[lastOptionValueId]) {
          const optionValueData = this.data.optionValues[lastOptionValueId];
          this.variant = {
            id: optionValueData.variantId,
            available: optionValueData.available,
            options: this.getSelectedOptions(),
            title: this.getSelectedOptions().join(' / ')
          };

          // Update URL and form input immediately with the variant ID
          this.updateUrl(evt);
          this.updateVariantInput();
          this.updateAddToCartButton();
          // ✅ CORRECTED FIX: Only show price immediately if we have formatted
          // data for the variant that matches ALL selected options (not just last one).
          // Cross-reference against the full variant list to verify the ID is correct.
          const selectedOptions = this.getSelectedOptions();
          const verifiedVariant = this.data.product.variants.find(
            v => v.options.every((val, index) => val === selectedOptions[index])
          );
          if (verifiedVariant && this.data.formatted && this.data.formatted[verifiedVariant.id]) {
            this.variant = verifiedVariant; // use the fully verified variant object
            this.updatePrice();
            this.updateUrl(evt);
            this.updateVariantInput();
            this.updateAddToCartButton();
          }
        }

        // Fetch the complete variant data
        this.fetchVariantData().then(() => {
          this.completeVariantChange(evt);
        });
      } else {
        // Fallback for products with < 250 variants
        this.variant = this.getSelectedVariant();
        this.completeVariantChange(evt);
      }
    }

    /**
     * Completes variant change after data is available
     * @param {object} evt - Event object.
     */
    completeVariantChange(evt) {
      if (this.variant) {
        // Only update media if it's NOT the initial page load
        if (!this.isInitialLoad) {
          this.updateMedia();
        }

        this.isInitialLoad = false; // Reset flag after first check
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Fetches variant data using the current option selection.
     * @returns {Promise}
     */
    async fetchVariantData() {
      try {
        const selectedOptionValueIds = this.getSelectedOptionValueIds();
        const params = `?option_values=${selectedOptionValueIds.join(',')}`;

        const response = await fetch(`${this.dataset.url}${params}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const scriptEl = doc.querySelector('variant-picker [type="application/json"]');

        if (scriptEl) {
          const newData = JSON.parse(scriptEl.textContent);

          // Replace optionValues with the new context
          if (newData.optionValues) {
            this.data.optionValues = newData.optionValues;
          }

          // The server returns the correct variant for our option_values
          if (newData.product && newData.product.selected_or_first_available_variant) {
            const newVariant = newData.product.selected_or_first_available_variant;

            // Add/update variant in our list
            const existingVariantIndex = this.data.product.variants.findIndex(v => v.id === newVariant.id);
            if (existingVariantIndex === -1) {
              this.data.product.variants.push(newVariant);
            } else {
              this.data.product.variants[existingVariantIndex] = newVariant;
            }

            // Update formatted data for this variant
            if (newData.formatted && newData.formatted[newVariant.id]) {
              this.data.formatted[newVariant.id] = newData.formatted[newVariant.id];
            }

            // Set this as our current variant
            this.variant = newVariant;
          }
        }
      } catch (error) {
        console.error('Error fetching variant data:', error);
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      const selectedOptions = this.getSelectedOptions();

      if (availabilityMethod === 'selection') {
        // For selection method, use optionValues to determine availability
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          const optionValueId = optionEl.dataset.optionValueId;
          if (optionValueId && this.data.optionValues && this.data.optionValues[optionValueId]) {
            const optionData = this.data.optionValues[optionValueId];
            VariantPicker.updateOptionAvailability(optionEl, true, optionData.available);
          } else {
            VariantPicker.updateOptionAvailability(optionEl, false, false);
          }
        });
      } else {
        // For downward method, use optionValues
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValueId = option.dataset.optionValueId;
            if (optionValueId && this.data.optionValues && this.data.optionValues[optionValueId]) {
              const optionData = this.data.optionValues[optionValueId];
              VariantPicker.updateOptionAvailability(option, true, optionData.available);
            } else {
              VariantPicker.updateOptionAvailability(option, false, false);
            }
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;

      if (window.BOLD && BOLD.pre) {
        BOLD.pre.events.emit("variant_changed", { variant: this.variant });
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      // Try to get variant from option values first (for high-variant products)
      const selectedOptionValueIds = this.getSelectedOptionValueIds();
      if (selectedOptionValueIds.length > 0 && this.data.optionValues) {
        // Get variant ID from the last selected option value
        const lastOptionValueId = selectedOptionValueIds[selectedOptionValueIds.length - 1];
        if (this.data.optionValues[lastOptionValueId]) {
          const optionValueData = this.data.optionValues[lastOptionValueId];
          const variantId = optionValueData.variantId;
          // Try to find variant in product.variants (may be limited to 250)
          let variant = this.data.product.variants.find(v => v.id === variantId);
          if (variant) {
            return variant;
          }
          // If variant not in list, create a minimal variant object with data from optionValues
          // This will be enhanced when fetchVariantData completes
          return {
            id: variantId,
            available: optionValueData.available,
            options: this.getSelectedOptions(),
            title: this.getSelectedOptions().join(' / '),
            featured_media: null,
            price: 0,
            compare_at_price: null
          };
        }
      }

      // Fallback to old method for products with < 250 variants
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }

    /**
     * Get selected option value IDs.
     * @returns {Array} Array of selected option value IDs.
     */
    getSelectedOptionValueIds() {
      const selectedIds = [];
      this.optionSelectors.forEach((selector) => {
        const selectedOption = selector.querySelector('.js-option:checked') ||
          selector.querySelector('.js-option[checked]');
        if (selectedOption && selectedOption.dataset.optionValueId) {
          selectedIds.push(selectedOption.dataset.optionValueId);
        }
      });
      return selectedIds;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
